import type { BoxStats } from "../evalDistributionModel";
import { formatDetailNumber } from "../evalRunResultModel";
import { userSeriesFirst, type EvalResultSeries } from "./evalCompareModel";

type OutcomeComparePoint = Record<string, number | string>;

/** 개수 칸의 key 다. 막대 높이는 비율이고, 사람이 읽는 숫자는 실제 Episode 수다. */
export function outcomeCountKey(seriesKey: string): string {
  return `${seriesKey}:count`;
}

// 막대 높이는 계열별 전체 대비 비율이다. baseline 은 Seed 를 합쳐서 Episode 수가 몇 배 많다.
// 개수로 높이를 정하면 "표본이 크다"가 "성적이 다르다"로 읽힌다. 계열이 하나뿐이면 개수로 정할
// 때와 높이 비율이 같다.
export function mergeOutcomes(items: readonly EvalResultSeries[]): OutcomeComparePoint[] {
  const orderedItems = userSeriesFirst(items);
  const labels: string[] = [];
  for (const item of orderedItems) {
    for (const outcome of item.summary.outcomeBreakdown) {
      if (!labels.includes(outcome.label)) labels.push(outcome.label);
    }
  }
  const totals = new Map(
    orderedItems.map((item) => [
      item.key,
      item.summary.outcomeBreakdown.reduce((sum, outcome) => sum + outcome.count, 0),
    ]),
  );
  return labels.map((label) => {
    const point: OutcomeComparePoint = { label };
    for (const item of orderedItems) {
      const found = item.summary.outcomeBreakdown.find((outcome) => outcome.label === label);
      if (found === undefined) continue;
      const total = totals.get(item.key) ?? 0;
      point[item.key] = total > 0 ? found.count / total : 0;
      point[outcomeCountKey(item.key)] = found.count;
    }
    return point;
  });
}

type CompareStatColumn = {
  key: string;
  label: string;
  value: (item: EvalResultSeries) => number | null;
  format: (value: number) => string;
  lowerIsBetter: boolean;
};

export type CompareStatGroup = {
  key: string;
  header: string | null;
  columns: CompareStatColumn[];
};

export function formatPercentStat(value: number): string {
  return `${formatDetailNumber(value * 100)}%`;
}

function formatMetricStat(value: number): string {
  const abs = Math.abs(value);
  return value.toFixed(abs < 1 ? 3 : abs < 10 ? 2 : 1);
}

function distributionStats(item: EvalResultSeries, key: string): BoxStats | null {
  return item.summary.distributions.find((dist) => dist.key === key)?.stats ?? null;
}

export function summaryStatGroups(
  items: readonly EvalResultSeries[],
  includeSampleCount: boolean = false,
): CompareStatGroup[] {
  const groups: CompareStatGroup[] = [
    {
      key: "success",
      header: null,
      columns: [
        {
          key: "success",
          label: "Success",
          value: (item) => item.summary.finalSuccessRate,
          format: formatPercentStat,
          lowerIsBetter: false,
        },
      ],
    },
  ];
  const keys: string[] = [];
  for (const item of userSeriesFirst(items)) {
    for (const dist of item.summary.distributions) {
      if (!keys.includes(dist.key)) keys.push(dist.key);
    }
  }
  for (const key of keys) {
    const statsFor = (item: EvalResultSeries) => distributionStats(item, key);
    const columns: CompareStatColumn[] = [
      {
        key: `dist:${key}:p50`,
        label: "p50",
        value: (item) => statsFor(item)?.median ?? null,
        format: formatMetricStat,
        lowerIsBetter: true,
      },
      {
        key: `dist:${key}:iqr`,
        label: "IQR",
        value: (item) => {
          const box = statsFor(item);
          return box === null ? null : box.q3 - box.q1;
        },
        format: formatMetricStat,
        lowerIsBetter: true,
      },
      {
        key: `dist:${key}:out`,
        label: "out%",
        value: (item) => {
          const box = statsFor(item);
          return box !== null && box.count > 0 ? (box.outliers.length / box.count) * 100 : null;
        },
        format: (value) => `${value.toFixed(1)}%`,
        lowerIsBetter: true,
      },
    ];
    if (includeSampleCount) {
      columns.push({
        key: `dist:${key}:count`,
        label: "n",
        value: (item) => statsFor(item)?.count ?? null,
        format: String,
        lowerIsBetter: false,
      });
    }
    groups.push({ key, header: key, columns });
  }
  return groups;
}

export function bestStatValue(
  items: readonly EvalResultSeries[],
  column: CompareStatColumn,
): number | null {
  const values = items.map(column.value).filter((value): value is number => value !== null);
  if (values.length === 0) return null;
  return column.lowerIsBetter ? Math.min(...values) : Math.max(...values);
}
